/**
 * <sd-rail> — prev/next buttons for a horizontally scrolling row.
 *
 * The row itself is the scroll container and stays keyboard-scrollable
 * (tabindex="0" + native arrow keys), so the buttons are a MOUSE CONVENIENCE
 * and are aria-hidden/tabindex="-1". Nothing is reachable only by clicking them.
 *
 * The buttons only exist when the row actually overflows: a ResizeObserver
 * watches the track and toggles a class on the host, so at 4-up on a wide
 * desktop (where nothing scrolls) they are not dead controls sitting on screen.
 * Each button also disables itself at its end of the travel.
 *
 * Scrolling uses the house easing via native smooth behaviour, and falls back to
 * an instant jump under prefers-reduced-motion.
 *
 * OPTIONAL AUTOPLAY (data-autoplay): advances on an interval, ALWAYS in one
 * direction. The cards are cloned once and appended, so when the scroll passes
 * the end of the original set the position is reset by exactly that set's width
 * — an instant, non-animated jump onto an identical frame, which is invisible.
 * The rail therefore never appears to rewind; it only ever travels forward.
 *
 * Cloning is safe for the video rail specifically because its <video> elements
 * are preload="none": a clone costs one poster image, not a second video
 * download. Clones are inert (aria-hidden, not focusable) so the loop never
 * duplicates content for assistive tech or the keyboard tab order.
 *
 * It is a courtesy, never a hijack — it pauses on hover, on focus within, while
 * a <sd-video> in the rail is playing, when the tab is hidden, and once the
 * visitor scrolls the rail themselves. It never starts at all under
 * prefers-reduced-motion or the store's "minimal" motion setting, and never
 * when nothing overflows.
 *
 * Markup contract:
 *   <sd-rail data-autoplay data-interval="4500">
 *     <button ref="prev" on:click="/prev">…</button>
 *     <div ref="track" tabindex="0">…cards…</div>
 *     <button ref="next" on:click="/next">…</button>
 *   </sd-rail>
 */

import { Component } from '@theme/component';

class SdRail extends Component {
  requiredRefs = ['track'];

  #abort = new AbortController();
  #ro = null;
  #reduce = false;

  connectedCallback() {
    super.connectedCallback();
    this.#reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.refs.track.addEventListener('scroll', this.#sync, {
      passive: true,
      signal: this.#abort.signal,
    });

    // Overflow can change with viewport, font loading, or images arriving.
    if ('ResizeObserver' in window) {
      this.#ro = new ResizeObserver(this.#sync);
      this.#ro.observe(this.refs.track);
      for (const child of this.refs.track.children) this.#ro.observe(child);
    }
    window.addEventListener('resize', this.#sync, { passive: true, signal: this.#abort.signal });

    this.#sync();
    this.#initAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#ro?.disconnect();
    this.#stop();
  }

  // ---- Autoplay -----------------------------------------------------------

  #timer = null;
  #paused = false;

  #initAutoplay() {
    if (!this.hasAttribute('data-autoplay')) return;

    const minimal = document.body.classList.contains('sd-motion-minimal');
    // A rail that moves on its own is exactly what reduced-motion asks not to
    // happen, so this is a hard gate, not a shortened duration.
    if (this.#reduce || minimal) return;

    this.interval = Math.max(1500, Number(this.dataset.interval) || 4500);
    const opts = { signal: this.#abort.signal };

    this.#cloneForLoop();

    // Pause while the visitor is engaging with the rail.
    this.addEventListener('pointerenter', () => this.#pause(true), opts);
    this.addEventListener('pointerleave', () => this.#pause(false), opts);
    this.addEventListener('focusin', () => this.#pause(true), opts);
    this.addEventListener('focusout', () => {
      if (!this.contains(document.activeElement)) this.#pause(false);
    }, opts);

    // A playing video owns the visitor's attention — never scroll it away.
    document.addEventListener('sd-video:play', () => this.#pause(true), opts);
    this.addEventListener('pause', () => this.#syncVideoPause(), { ...opts, capture: true });

    // Off-screen or background tabs: stop burning frames (and don't "advance"
    // invisibly, which would dump the visitor mid-rail on return).
    document.addEventListener('visibilitychange', () => this.#restart(), opts);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          for (const e of entries) this.#pause(!e.isIntersecting, 'offscreen');
        },
        { threshold: 0.25 }
      ).observe(this);
    }

    // Once the visitor drives the rail themselves, stop competing with them.
    this.refs.track.addEventListener('pointerdown', () => this.#stop(), opts);
    this.refs.track.addEventListener('keydown', () => this.#stop(), opts);

    this.#restart();
  }

  #syncVideoPause() {
    // Resume only when nothing in the rail is still playing.
    const playing = this.querySelector('[data-playing]');
    if (!playing) this.#pause(false);
  }

  #pause(state, reason) {
    if (reason === 'offscreen') this.offscreen = state;
    else this.#paused = state;
    this.#restart();
  }

  #restart() {
    clearInterval(this.#timer);
    this.#timer = null;
    // interval is only set once autoplay has passed its motion gate; without it
    // this rail is arrow-driven only and must never start a timer.
    if (!this.interval) return;
    if (this.stopped || this.#paused || this.offscreen || document.hidden) return;
    if (!this.classList.contains('sd-rail--scrollable')) return;
    this.#timer = setInterval(() => this.#advance(), this.interval);
  }

  #stop() {
    this.stopped = true;
    clearInterval(this.#timer);
    this.#timer = null;
  }

  /**
   * Duplicate the original cards once so the rail can run forward forever.
   *
   * #loopWidth is the width of the ORIGINAL set. Once scrollLeft passes it the
   * visitor is looking at the clones, which are pixel-identical to the
   * originals, so subtracting that width lands on the same frame — the reset is
   * undetectable. Clones are hidden from assistive tech and removed from the
   * tab order so the loop does not duplicate content.
   */
  #cloneForLoop() {
    const t = this.refs.track;
    const originals = Array.from(t.children);
    if (originals.length < 2) return;

    // Only worth cloning when the set actually overflows; otherwise the rail is
    // static and the clones would create phantom scroll width.
    if (t.scrollWidth - t.clientWidth <= 2) return;

    for (const node of originals) {
      const clone = node.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.sdClone = '';
      // Nothing inside a clone may be reachable by keyboard.
      for (const el of clone.querySelectorAll('a, button, video, [tabindex]')) {
        el.setAttribute('tabindex', '-1');
      }
      // A cloned <video> must never fetch or play: it is decoration standing in
      // for the real card, and two copies of the same review playing at once
      // would be a mess. The poster still renders, so the clone looks identical.
      for (const v of clone.querySelectorAll('video')) {
        v.removeAttribute('src');
        v.querySelectorAll('source').forEach((s) => s.remove());
        v.preload = 'none';
        v.muted = true;
      }
      t.appendChild(clone);
    }

    this.looped = true;
    this.#measureLoop();
    // The clones arrived after connectedCallback wired the observer, so they
    // are not being watched yet — without this a late-loading poster would not
    // trigger a re-measure and the loop length would drift.
    for (const child of t.children) this.#ro?.observe(child);
    this.#sync();
  }

  #measureLoop() {
    const t = this.refs.track;
    const kids = Array.from(t.children).filter((c) => !('sdClone' in c.dataset));
    if (!kids.length) return;
    const first = kids[0].getBoundingClientRect();
    const last = kids[kids.length - 1].getBoundingClientRect();
    const gap = parseFloat(getComputedStyle(t).columnGap) || 0;
    this.loopWidth = last.right - first.left + gap;
  }

  /** One step forward — ALWAYS forward. Never scrolls back to the start. */
  #advance() {
    const t = this.refs.track;

    if (this.looped) {
      // Past the original set? Rebase by exactly its width onto the identical
      // clone frame. behavior:auto (not smooth) so the jump is instantaneous
      // and therefore invisible.
      if (this.loopWidth && t.scrollLeft >= this.loopWidth) {
        t.scrollTo({ left: t.scrollLeft - this.loopWidth, behavior: 'auto' });
      }
      this.#scrollBy(1);
      return;
    }

    // No clones (too few cards, or no overflow at clone time): fall back to the
    // wrap so a short rail still cycles rather than dead-ending.
    const atEnd = t.scrollLeft >= t.scrollWidth - t.clientWidth - 2;
    if (atEnd) t.scrollTo({ left: 0, behavior: this.#reduce ? 'auto' : 'smooth' });
    else this.#scrollBy(1);
  }

  /** One card's worth of travel, falling back to most of the viewport. */
  #step() {
    const kids = this.refs.track.children;
    if (kids.length > 1) {
      const a = kids[0].getBoundingClientRect();
      const b = kids[1].getBoundingClientRect();
      const d = b.left - a.left;
      if (d > 0) return d;
    }
    return Math.max(240, this.refs.track.clientWidth * 0.8);
  }

  #scrollBy(dir) {
    this.refs.track.scrollBy({
      left: dir * this.#step(),
      behavior: this.#reduce ? 'auto' : 'smooth',
    });
  }

  prev() {
    // On a looped rail, stepping back from the start would hit a hard edge.
    // Rebase forward onto the identical clone frame first, so back is endless
    // too — the arrows stay usable in both directions forever.
    const t = this.refs.track;
    if (this.looped && this.loopWidth && t.scrollLeft < this.#step()) {
      t.scrollTo({ left: t.scrollLeft + this.loopWidth, behavior: 'auto' });
    }
    this.#scrollBy(-1);
  }

  next() { this.#scrollBy(1); }

  #sync = () => {
    const t = this.refs.track;
    // 2px tolerance: sub-pixel layout can leave a phantom 0.5px of overflow.
    const overflows = t.scrollWidth - t.clientWidth > 2;
    const wasScrollable = this.classList.contains('sd-rail--scrollable');
    this.classList.toggle('sd-rail--scrollable', overflows);
    // Overflow can appear late (images/fonts arriving) or vanish on resize;
    // autoplay is gated on it, so re-evaluate the timer when it flips.
    if (overflows !== wasScrollable && this.hasAttribute('data-autoplay')) this.#restart();

    // Clones change the track's width, so the loop length must be re-measured
    // whenever layout shifts.
    if (this.looped) this.#measureLoop();

    // On a looped rail the ends are not real — the clones make it endless in
    // both handling and appearance — so the arrows must never disable.
    if (this.looped) {
      if (this.refs.prev) this.refs.prev.disabled = false;
      if (this.refs.next) this.refs.next.disabled = false;
      return;
    }

    const atStart = t.scrollLeft <= 2;
    const atEnd = t.scrollLeft >= t.scrollWidth - t.clientWidth - 2;
    if (this.refs.prev) this.refs.prev.disabled = !overflows || atStart;
    if (this.refs.next) this.refs.next.disabled = !overflows || atEnd;
  };
}

if (!customElements.get('sd-rail')) customElements.define('sd-rail', SdRail);
